import { z } from 'zod';
/**
 * Contracts for the admin conversation-rail subscription surface
 * (`GET /api/conversations/:id/subscription`,
 * `POST /api/conversations/:id/cancel-subscription`). These DTOs are the single
 * source of truth shared by the admin API routes and the admin-web module, and
 * mirror the `@wit-support/payment` `BillingSummary` domain shape.
 *
 * IMPORTANT: all money amounts are in **cents** (integers). Formatting (currency
 * symbol, decimals) happens only at the UI edge.
 */
/** Lifecycle status of an active subscription as reported upstream. */
export const SubscriptionStatus = z.enum(['TRIAL', 'ACTIVE', 'PAST_DUE', 'UNPAID']);
/** Billing period unit as reported upstream. */
export const SubscriptionPeriod = z.enum(['DAY', 'WEEK', 'MONTH', 'YEAR']);
/**
 * A billing period descriptor, e.g. `{ period: 'MONTH', interval: 1 }`. Both
 * fields are nullable — a subscription with no trial reports
 * `{ period: null, interval: null }`.
 */
export const BillingPeriodDTO = z.object({
    period: SubscriptionPeriod.nullable(),
    interval: z.number().nullable(),
});
/** One active subscription summary. */
export const ActiveSubscriptionDTO = z.object({
    subscriptionId: z.string(),
    stripeSubscriptionId: z.string().optional(),
    gateway: z.string().optional(),
    planKey: z.string().optional(),
    status: SubscriptionStatus,
    trial: BillingPeriodDTO,
    billing: BillingPeriodDTO,
});
/** Aggregated money for one bucket (subscriptions / additionalPurchases). Cents. */
export const MoneyBucketDTO = z.object({
    succeededChargesCount: z.number(),
    grossAmount: z.number(),
    refundedAmount: z.number(),
    disputedAmount: z.number(),
    netAmount: z.number(),
});
/** The `money.total` rollup. Cents. */
export const MoneyTotalDTO = z.object({
    grossAmount: z.number(),
    refundedAmount: z.number(),
    disputedAmount: z.number(),
    netAmount: z.number(),
});
/** Rich billing summary for a user. Mirrors `payment.BillingSummary`. Money in cents. */
export const SubscriptionSummaryDTO = z.object({
    userId: z.string(),
    currency: z.string(),
    multiCurrency: z.boolean(),
    currencies: z.array(z.string()),
    subscriptions: z.object({
        activeCount: z.number(),
        activeByStatus: z.record(z.string(), z.number()),
        cancelledCount: z.number(),
        active: z.array(ActiveSubscriptionDTO),
    }),
    billingCycles: z.object({
        passed: z.number(),
        succeeded: z.number(),
    }),
    money: z.object({
        subscriptions: MoneyBucketDTO,
        additionalPurchases: MoneyBucketDTO,
        total: MoneyTotalDTO,
    }),
});
/**
 * Response for `GET /api/conversations/:id/subscription`.
 * - `configured: false` → payment service not wired (PAYMENT_API_BASE_URL unset).
 * - `summary: null` → no billing record for the user, or no resolved wit_user_id,
 *   or a fail-soft upstream error (the rail simply shows "no subscriptions").
 */
export const SubscriptionResponse = z.object({
    configured: z.boolean(),
    summary: SubscriptionSummaryDTO.nullable(),
});
/** Aggregate result of a cancel-all operation. Mirrors `payment.CancelAllResult` (no per-item detail). */
export const CancelSubscriptionResultDTO = z.object({
    total: z.number(),
    cancelled: z.number(),
    alreadyCancelled: z.number(),
    bypassed: z.number(),
    failed: z.number(),
});
/** Optional cancel request body. */
export const CancelSubscriptionRequest = z.object({
    reason: z.string().trim().min(1).max(500).optional(),
});
/**
 * Response for `POST /api/conversations/:id/cancel-subscription`.
 * - `ok: false` with a `reason` when there's no resolved wit_user_id
 *   (`no_user`), payment isn't configured (`not_configured`), or the upstream
 *   payment service failed (`upstream_error`, fail-soft — HTTP 200 so the audit
 *   still records the failed attempt and the UI shows a friendly message).
 * - `ok: false`, `reason: 'partial_failure'` when the cancel-all ran (HTTP 200)
 *   but reported `failed > 0`: at least one subscription may still be billing,
 *   so this is NOT a success — the `result` breakdown is carried so the UI can
 *   show how many cancelled vs failed. (Without this, a `{cancelled:0, failed:N}`
 *   aggregate would wrongly toast "Отменено: 0" as success.)
 * - `ok: true` with the aggregate `result` ONLY when nothing failed.
 */
// A plain union (NOT z.discriminatedUnion): the failure arms share `ok: false`,
// which a discriminated union rejects ("Duplicate discriminator value") the
// moment it's parsed. The arms are still mutually exclusive via `ok` + `reason`,
// so a plain union parses each shape unambiguously.
export const CancelSubscriptionResponse = z.union([
    z.object({
        ok: z.literal(false),
        reason: z.enum(['no_user', 'not_configured']),
    }),
    z.object({
        ok: z.literal(false),
        reason: z.literal('upstream_error'),
        detail: z.string().optional(),
    }),
    z.object({
        ok: z.literal(false),
        reason: z.literal('partial_failure'),
        result: CancelSubscriptionResultDTO,
    }),
    z.object({
        ok: z.literal(true),
        result: CancelSubscriptionResultDTO,
    }),
]);
// ---------------------------------------------------------------------------
// Subscription actions (retaining-funnel history) — GET /:id/subscription-actions
// ---------------------------------------------------------------------------
/** One choice at a retaining-funnel step (the cancel reason lives on `what-reason`). */
export const RetainingChoiceDTO = z.object({
    step: z.string(),
    choiceId: z.number(),
    choiceTitle: z.string(),
    timestamp: z.string(),
});
/** Retaining-funnel state captured with an action; `null` when the action was outside the funnel. */
export const RetainingJourneyDTO = z.object({
    startedAt: z.string(),
    updatedAt: z.string(),
    currentStep: z.string(),
    funnel: z.string().nullable(),
    completedSteps: z.array(z.string()),
    choices: z.array(RetainingChoiceDTO),
});
/** One retaining/cancellation-funnel action record (newest-first as returned). */
export const SubscriptionActionDTO = z.object({
    id: z.string(),
    subscriptionId: z.string(),
    action: z.string(),
    result: z.enum(['success', 'error']),
    message: z.string().nullable(),
    retainingJourney: RetainingJourneyDTO.nullable(),
    createdAt: z.string(),
});
/**
 * Response for `GET /api/conversations/:id/subscription-actions`.
 * - `configured: false` → payment service not wired (PAYMENT_API_BASE_URL unset).
 * - `actions: []` → no records, no resolved wit_user_id, or a fail-soft upstream
 *   error (the rail simply hides the card).
 */
export const SubscriptionActionsResponse = z.object({
    configured: z.boolean(),
    actions: z.array(SubscriptionActionDTO),
});
//# sourceMappingURL=subscription.js.map